import React, { useEffect, useState, useMemo } from 'react';
import { useChatStore } from '../../store/chatStore';
import { ChevronDown, MessageSquare, Search, Plus, Calendar } from 'lucide-react';

const ChatHistoryDropdown = ({ userId }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const {
        sessions,
        currentSessionId,
        loadSessions,
        setCurrentSession,
        createNewSession,
        historyLoading,
        getTodaySession
    } = useChatStore();

    useEffect(() => {
        if (userId) loadSessions(userId);
    }, [userId, loadSessions]);

    // Initial load of today's session if nothing active
    useEffect(() => {
        if (userId && !currentSessionId && sessions.length === 0 && !historyLoading) {
            getTodaySession(userId);
        }
    }, [userId, currentSessionId, sessions.length, historyLoading, getTodaySession]);


    const currentSession = sessions.find(s => s.id === currentSessionId);

    const toggleDropdown = () => setIsOpen(!isOpen);

    const handleNewChat = async (e) => {
        e.stopPropagation();
        await createNewSession(userId);
        setIsOpen(false);
    };

    const handleSelectSession = (sessionId) => {
        setCurrentSession(sessionId);
        setIsOpen(false);
    }

    const groupedSessions = useMemo(() => {
        const query = searchQuery.toLowerCase();
        const filtered = sessions.filter(s =>
            s.title.toLowerCase().includes(query)
        );

        const groups = {
            today: [],
            yesterday: [],
            last7days: [],
            older: []
        };

        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);

        filtered.forEach(session => {
            const date = new Date(session.last_message_at || session.created_at);

            if (date.toDateString() === today.toDateString()) {
                groups.today.push(session);
            } else if (date.toDateString() === yesterday.toDateString()) {
                groups.yesterday.push(session);
            } else if (date > new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)) {
                groups.last7days.push(session);
            } else {
                groups.older.push(session);
            }
        });

        return groups;
    }, [sessions, searchQuery]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const close = (e) => {
            if (isOpen && !e.target.closest('.chat-history-dropdown')) {
                setIsOpen(false);
            }
        }
        document.addEventListener('click', close);
        return () => document.removeEventListener('click', close);
    }, [isOpen]);

    return (
        <div className="relative chat-history-dropdown z-50">
            <button
                onClick={toggleDropdown}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/10 transition-colors"
                title="Switch chat"
            >
                <div className="flex flex-col items-start text-left">
                    <span className="text-sm font-medium dark:text-white flex items-center gap-2">
                        {currentSession?.title || 'New Chat'}
                        <ChevronDown size={14} className={`opacity-50 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </span>
                    <span className="text-[10px] opacity-50 dark:text-slate-400">
                        {currentSession?.message_count || 0} messages
                    </span>
                </div>
            </button>

            {isOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 max-h-[500px] flex flex-col bg-white dark:bg-[#0F172A] rounded-xl shadow-2xl border border-slate-200 dark:border-white/10 overflow-hidden animate-in fade-in zoom-in-95 duration-100">

                    {/* Header + Search */}
                    <div className="p-3 border-b border-slate-100 dark:border-white/5 space-y-3">
                        <div className="flex justify-between items-center">
                            <span className="text-xs font-semibold opacity-50 uppercase tracking-wider">History</span>
                            <button
                                onClick={handleNewChat}
                                className="text-xs flex items-center gap-1 text-secondary hover:text-secondary/80 bg-secondary/10 px-2.5 py-1.5 rounded-lg font-medium transition-colors"
                            >
                                <Plus size={14} /> New Chat
                            </button>
                        </div>
                        <div className="relative">
                            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 opacity-50" />
                            <input
                                type="text"
                                placeholder="Search chats..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-8 pr-3 py-2 text-xs rounded-lg bg-slate-50 dark:bg-black/20 dark:text-white border-none focus:ring-1 focus:ring-secondary/50 placeholder:text-slate-500 dark:placeholder:text-slate-400"
                            />
                        </div>
                    </div>

                    {/* List */}
                    <div className="overflow-y-auto flex-1 p-2 space-y-4 custom-scrollbar">

                        {groupedSessions.today.length > 0 && (
                            <div className="space-y-1">
                                <h4 className="px-2 text-[10px] font-bold opacity-40 uppercase">Today</h4>
                                {groupedSessions.today.map(session => (
                                    <SessionItem
                                        key={session.id}
                                        session={session}
                                        active={session.id === currentSessionId}
                                        onClick={() => handleSelectSession(session.id)}
                                    />
                                ))}
                            </div>
                        )}

                        {groupedSessions.yesterday.length > 0 && (
                            <div className="space-y-1">
                                <h4 className="px-2 text-[10px] font-bold opacity-40 uppercase">Yesterday</h4>
                                {groupedSessions.yesterday.map(session => (
                                    <SessionItem
                                        key={session.id}
                                        session={session}
                                        active={session.id === currentSessionId}
                                        onClick={() => handleSelectSession(session.id)}
                                    />
                                ))}
                            </div>
                        )}

                        {groupedSessions.last7days.length > 0 && (
                            <div className="space-y-1">
                                <h4 className="px-2 text-[10px] font-bold opacity-40 uppercase">Previous 7 Days</h4>
                                {groupedSessions.last7days.map(session => (
                                    <SessionItem
                                        key={session.id}
                                        session={session}
                                        active={session.id === currentSessionId}
                                        onClick={() => handleSelectSession(session.id)}
                                    />
                                ))}
                            </div>
                        )}

                        {groupedSessions.older.length > 0 && (
                            <div className="space-y-1">
                                <h4 className="px-2 text-[10px] font-bold opacity-40 uppercase">Older</h4>
                                {groupedSessions.older.map(session => (
                                    <SessionItem
                                        key={session.id}
                                        session={session}
                                        active={session.id === currentSessionId}
                                        onClick={() => handleSelectSession(session.id)}
                                    />
                                ))}
                            </div>
                        )}

                        {sessions.length === 0 && !historyLoading && (
                            <div className="px-4 py-8 text-center opacity-50 text-xs">
                                No history yet
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const SessionItem = ({ session, active, onClick }) => (
    <button
        onClick={onClick}
        className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-start gap-2 transition-colors ${active
            ? 'bg-secondary/10 text-secondary'
            : 'hover:bg-slate-100 dark:hover:bg-white/5 opacity-80 hover:opacity-100 text-slate-700 dark:text-slate-300'
            }`}
    >
        <MessageSquare size={14} className={`mt-0.5 shrink-0 ${active ? 'fill-current' : 'opacity-50'}`} />
        <span className="truncate flex-1">{session.title}</span>
    </button>
)

export default ChatHistoryDropdown;
